import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, withModifiers as _withModifiers, withKeys as _withKeys, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "page-shell" }
const _hoisted_2 = { class: "px-2" }
const _hoisted_3 = { class: "hero-panel" }
const _hoisted_4 = { class: "hero-metrics subscription-filters" }
const _hoisted_5 = ["onClick", "onKeydown"]
const _hoisted_6 = { class: "metric-content" }
const _hoisted_7 = { class: "metric-label" }
const _hoisted_8 = { class: "metric-value" }
const _hoisted_9 = {
  key: 2,
  class: "list-section"
}
const _hoisted_10 = {
  key: 0,
  class: "px-2"
}
const _hoisted_11 = { class: "list-title" }
const _hoisted_12 = { class: "state-card empty-wrap" }
const _hoisted_13 = { class: "empty-icon-wrap" }
const _hoisted_14 = { class: "empty-title" }

import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import AppCard from "@/shared/components/ui/AppCard.vue";
import { fetchCustomerSubscriptions } from "@/shared/services/customers.api";
import SubscriptionListCard from "@/modules/customer/components/SubscriptionListCard.vue";

type SubscriptionStatus = "Active" | "Paused" | "Inactive" | "All";

interface SubscriptionItem {
  id: string | number;
  packageName: string;
  planAmount: number | null;
  billingInterval: string;
  nextBillingDate: string | null;
  status: SubscriptionStatus;
  vendorName: string;
  vendorLogo: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CustomerSubscriptionPage',
  setup(__props) {

const router = useRouter();

const activeTab = ref<SubscriptionStatus>("Active");
const isLoading = ref(false);
const errorMessage = ref("");
const subscriptions = ref<SubscriptionItem[]>([]);

const filteredSubscriptions = computed(() => {
  return activeTab.value === "All"
    ? subscriptions.value
    : subscriptions.value.filter((item) => item.status === activeTab.value);
});

const subscriptionCounts = computed(() => {
  return subscriptions.value.reduce(
    (acc, item) => {
      acc.total += 1;
      if (item.status === "Active") acc.active += 1;
      if (item.status === "Paused") acc.paused += 1;
      if (item.status === "Inactive") acc.inactive += 1;
      return acc;
    },
    { total: 0, active: 0, paused: 0, inactive: 0 }
  );
});

const listSectionTitle = computed(() => {
  const label = activeTab.value.toLowerCase();
  return `${label.charAt(0).toUpperCase() + label.slice(1)} subscriptions`;
});

const heroMetricItems = computed(() => [
  {
    key: "Active" as const,
    label: "Active",
    value: subscriptionCounts.value.active,
    icon: "mdi-check-circle-outline",
    iconColor: "success",
  },
  {
    key: "Paused" as const,
    label: "Paused",
    value: subscriptionCounts.value.paused,
    icon: "mdi-pause-circle-outline",
    iconColor: "warning",
  },
  {
    key: "Inactive" as const,
    label: "Inactive",
    value: subscriptionCounts.value.inactive,
    icon: "mdi-close-circle-outline",
    iconColor: "grey",
  },
  {
    key: "All" as const,
    label: "All",
    value: subscriptionCounts.value.total,
    icon: "mdi-view-grid-outline",
    iconColor: "primary",
  },
]);

function normalizeStatus(status: unknown): SubscriptionStatus {
  const value = String(status ?? "")
    .trim()
    .toLowerCase();
  if (value === "paused" || value === "pending") return "Paused";
  if (value === "inactive" || value === "cancelled" || value === "canceled")
    return "Inactive";
  return "Active";
}

function handleOpen(id: string | number) {
  router
    .push({ name: "customer.subscriptions.detail", params: { id } })
    .catch(() => undefined);
}

async function loadSubscriptions() {
  isLoading.value = true;
  errorMessage.value = "";

  try {
    const response: any = await fetchCustomerSubscriptions();
    const list = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response)
      ? response
      : [];

    subscriptions.value = list.map((item: any) => ({
      id: item.id,
      packageName: item.package_name ?? "",
      planAmount: item.plan_amount ?? null,
      billingInterval: item.billing_interval ?? item.plan?.type_name ?? "",
      nextBillingDate: item.next_billing_date ?? null,
      status: normalizeStatus(item.status),
      vendorName: item.vendor?.name ?? "",
      vendorLogo: item.vendor?.logo ?? "",
    }));
  } catch (err) {
    console.error("Failed to load subscriptions", err);
    errorMessage.value = "Unable to load subscriptions.";
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  void loadSubscriptions();
});

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_alert = _resolveComponent("v-alert")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(heroMetricItems.value, (item) => {
            return (_openBlock(), _createElementBlock("div", {
              key: item.key,
              class: _normalizeClass(["metric-card", [{ 'metric-card--selected': activeTab.value === item.key }]]),
              role: "button",
              tabindex: "0",
              onClick: ($event: any) => (activeTab.value = item.key),
              onKeydown: [
                _withKeys(_withModifiers(($event: any) => (activeTab.value = item.key), ["prevent"]), ["enter"]),
                _withKeys(_withModifiers(($event: any) => (activeTab.value = item.key), ["prevent"]), ["space"])
              ]
            }, [
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("div", {
                  class: _normalizeClass(["metric-icon", `metric-icon--${item.key.toLowerCase()}`])
                }, [
                  _createVNode(_component_v_icon, {
                    size: "18",
                    color: item.iconColor
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(item.icon), 1)
                    ]),
                    _: 2
                  }, 1032, ["color"])
                ], 2),
                _createElementVNode("span", _hoisted_7, _toDisplayString(item.label), 1)
              ]),
              _createElementVNode("strong", _hoisted_8, _toDisplayString(item.value), 1)
            ], 42, _hoisted_5))
          }), 128))
        ])
      ])
    ]),
    (isLoading.value)
      ? (_openBlock(), _createBlock(_component_v_row, {
          key: 0,
          dense: ""
        }, {
          default: _withCtx(() => [
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(3, (index) => {
              return _createVNode(_component_v_col, {
                key: index,
                cols: "12"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_skeleton_loader, {
                    class: "subscription-skeleton",
                    type: "article"
                  })
                ]),
                _: 1
              })
            }), 64))
          ]),
          _: 1
        }))
      : (errorMessage.value)
        ? (_openBlock(), _createBlock(_component_v_row, {
            key: 1,
            dense: ""
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_col, { cols: "12" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_alert, {
                    class: "state-card",
                    type: "error",
                    variant: "tonal"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(errorMessage.value), 1)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }))
        : (_openBlock(), _createElementBlock("div", _hoisted_9, [
            (filteredSubscriptions.value.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                  _createElementVNode("h2", _hoisted_11, _toDisplayString(listSectionTitle.value), 1)
                ]))
              : _createCommentVNode("", true),
            (!filteredSubscriptions.value.length)
              ? (_openBlock(), _createBlock(_component_v_row, {
                  key: 1,
                  dense: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_col, { cols: "12" }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_12, [
                          _createElementVNode("div", _hoisted_13, [
                            _createVNode(_component_v_icon, {
                              size: "28",
                              color: "primary"
                            }, {
                              default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
                                _createTextVNode("mdi-calendar-check-outline", -1)
                              ]))]),
                              _: 1
                            })
                          ]),
                          _createElementVNode("div", _hoisted_14, " No " + _toDisplayString(activeTab.value.toLowerCase()) + " subscriptions ", 1),
                          _cache[1] || (_cache[1] = _createElementVNode("div", { class: "empty-subtitle" }, " Try another tab or check back later for updated plans. ", -1))
                        ])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }))
              : (_openBlock(), _createBlock(_component_v_row, {
                  key: 2,
                  dense: ""
                }, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredSubscriptions.value, (subscription) => {
                      return (_openBlock(), _createBlock(_component_v_col, {
                        key: subscription.id,
                        cols: "12"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(SubscriptionListCard, {
                            item: subscription,
                            onClick: ($event: any) => (handleOpen(subscription.id))
                          }, null, 8, ["item", "onClick"])
                        ]),
                        _: 2
                      }, 1024))
                    }), 128))
                  ]),
                  _: 1
                }))
          ]))
  ]))
}
}

})